import { useEffect, useState } from "react"
import { useMountedState } from "react-use"

import { AudioSourceInfo, InputDeviceManager, VideoSourceInfo } from "modules/Call/sdk"
import { VoximplantInputDevice } from "modules/Call/InputDevices/VoximplantInputDevice"

type SourceInfo = VideoSourceInfo | AudioSourceInfo

interface InputDeviceValues {
    loading: boolean
    list: SourceInfo[]
    selected?: SourceInfo
    error?: Error
    enabled: boolean
    accessed: boolean
}

interface InputDeviceHandlers {
    init(): Promise<boolean>
    selectDevice(id: number | string): void
    enable(on: boolean): void
    reload(): Promise<void>
}

export function useInputDevice<T extends InputDeviceManager>(
    voximplantInput: VoximplantInputDevice<T>
): [InputDeviceValues, InputDeviceHandlers] {
    const isMounted = useMountedState()
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<Error>()
    const [list, setList] = useState<SourceInfo[]>([])
    const [selected, setSelected] = useState<SourceInfo>()
    const [enabled, setEnabled] = useState(voximplantInput.enabled)
    const [accessed, setAccessed] = useState(false)

    async function init() {
        const accessed = await voximplantInput.getAccess()
        if (!accessed) return false
        await reload()
        setAccessed(true)
        return accessed
    }

    async function reload() {
        if (!isMounted()) return
        setLoading(true)
        await voximplantInput.reload()
        if (!isMounted()) return
        const oldDevices = list.map((device) => device.id).join(",")
        const newDevices = voximplantInput.list.map((device) => device.id).join(",")
        if (oldDevices === newDevices) {
            setLoading(false)
            return
        }
        setList(voximplantInput.list)
        setSelected(voximplantInput.selected)
        setError(voximplantInput.error)
        setLoading(false)
    }

    function selectDevice(id: SourceInfo["id"]) {
        voximplantInput.select(id)
        setSelected(voximplantInput.selected)
    }

    function enable(on: boolean) {
        voximplantInput.enabled = on
        setEnabled(voximplantInput.enabled)
    }

    useEffect(() => {
        function reloadDevices() {
            void reload()
        }

        VoximplantInputDevice.onChangeListener(reloadDevices)
        return () => {
            VoximplantInputDevice.offChangeListener(reloadDevices)
        }
    }, [])

    useEffect(() => {
        if (!accessed) return
        if (!list.length) {
            enable(false)
            return
        }
        if (selected && list.includes(selected)) return
        selectDevice(list[0].id)
    }, [list.map((device) => device.id).join(",")])

    return [
        {
            loading,
            error,
            list,
            accessed,
            selected,
            enabled,
        },
        {
            selectDevice,
            enable,
            init,
            reload,
        },
    ]
}
